// DraftPhotoUploadControls — shared photo affordance bar used by
// the top-level draft position editor (#588 DraftItemEditRow) and
// the box-nested editor (#1151 DraftBoxItemRow). Keeps the upload /
// paste-from-clipboard / clear / retry buttons, the hidden file
// input, and the upload/clipboard inline-error spans in one place
// so both rows stay in lockstep on i18n keys and disabled semantics.
//
// @aiInvariant The paste button MUST stay wrapped in a `<span onPaste>`
//   element. The wrapper has no `tabIndex`, so it does not change
//   tab order or hijack `onPaste` from sibling text inputs/textareas
//   inside the same card. Forwarding `onPaste` to `<Button>` directly
//   bypasses this scoping.
// @aiInvariant `photoActionsDisabled` (usually `uploading || frozen`)
//   gates upload + paste; clear has its own `clearDisabled` (usually
//   just `frozen`) because the user must be able to revoke a
//   not-yet-finished upload that is stuck on `frozen=false`.

import { type ClipboardEvent as ReactClipboardEvent, type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton } from '../../../ui/index.js';
import type { ClipboardExtractError } from '../clipboardExtract.js';

export interface DraftPhotoUploadControlsProps {
  /** Preview node (thumbnail or placeholder) supplied by the parent so
   *  each row keeps its own dimensions (72×54 vs 56×42). */
  preview: ReactNode;
  /** Whether a server-stored photo exists (drives "Replace" wording). */
  hasStoredPhoto: boolean;
  /** Whether a local blob preview exists (also drives "Replace" wording
   *  and the clear button visibility). */
  hasLocalPreview: boolean;
  uploading: boolean;
  uploadError: string | null;
  clipboardError: ClipboardExtractError | null;
  /** Disabled state for upload + paste buttons. Usually
   *  `uploading || frozen`. */
  photoActionsDisabled: boolean;
  /** Disabled state for the clear (X) button. Usually `frozen` alone. */
  clearDisabled: boolean;
  onSelectFile: (file: File) => void;
  onClear: () => void;
  onClipboardButtonClick: () => void;
  onClipboardPaste: (e: ReactClipboardEvent<HTMLElement>) => void;
  /** Optional retry affordance — only the top-level row uses it. The
   *  box-item row deliberately omits retry because it has no
   *  selectedFile state to retry from. */
  canRetry?: boolean;
  onRetry?: () => void;
}

export function DraftPhotoUploadControls({
  preview,
  hasStoredPhoto,
  hasLocalPreview,
  uploading,
  uploadError,
  clipboardError,
  photoActionsDisabled,
  clearDisabled,
  onSelectFile,
  onClear,
  onClipboardButtonClick,
  onClipboardPaste,
  canRetry = false,
  onRetry,
}: DraftPhotoUploadControlsProps) {
  const { t } = useTranslation(['requests', 'common']);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasPhotoSurface = hasStoredPhoto || hasLocalPreview;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelectFile(file);
          e.target.value = '';
        }}
      />
      {preview}
      <Button
        kind="ghost"
        size="sm"
        icon={hasPhotoSurface ? 'refresh-cw' : 'upload-cloud'}
        disabled={photoActionsDisabled}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading
          ? t('requests:draft.photoUploading')
          : hasPhotoSurface
            ? t('requests:draft.photoReplace')
            : t('requests:draft.photoUpload')}
      </Button>
      <span style={{ display: 'inline-flex' }} onPaste={onClipboardPaste}>
        <Button
          kind="ghost"
          size="sm"
          icon="clipboard-paste"
          disabled={photoActionsDisabled}
          aria-label={t('requests:draft.photoFromClipboard')}
          onClick={onClipboardButtonClick}
        >
          {t('requests:draft.photoFromClipboard')}
        </Button>
      </span>
      {hasPhotoSurface && (
        <IconButton
          icon="x"
          size={24}
          label={t('requests:draft.photoRemove')}
          onClick={onClear}
          disabled={clearDisabled}
        />
      )}
      {canRetry && onRetry && (
        <Button kind="secondary" size="sm" icon="rotate-ccw" onClick={onRetry}>
          {t('requests:draft.photoRetry')}
        </Button>
      )}
      {uploadError && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {uploadError}
        </span>
      )}
      {clipboardError && (
        <span
          role="alert"
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {t(`requests:draft.photoClipboard.${clipboardError}`)}
        </span>
      )}
    </div>
  );
}
